import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";

import { RegionParlorsContainer } from "@/containers/RegionParlorsContainer";
import {
  buildRegionPageProps,
  RegionPageProps,
} from "@/utils/buildRegionPageProps";
import { listRegionPageRegions } from "@/utils/regionFacilities";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

/**
 * 지역 장례식장 페이지. 장례식장이 메인이고 인근 화장장, 장지를 같은 페이지에 묶는다.
 * 기획: wiki/plans/region-parlor-subdomain-seo-plan-2026-08.md
 *
 * URL 단위는 시군구. 장례식장이 0곳인 시군구도 앵커 좌표가 있으면 생성하고,
 * 목록을 인근 시설 거리순으로 채운다(listRegionPageRegions).
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;

  if (skipBuildResult) {
    return skipBuildResult;
  }

  return {
    paths: listRegionPageRegions().map((region) => ({
      params: { sido: region.sido, sigungu: region.sigungu },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const sido = params?.sido;
  const sigungu = params?.sigungu;

  if (typeof sido !== "string" || typeof sigungu !== "string") {
    return { notFound: true };
  }

  const props = buildRegionPageProps(sido, sigungu);

  if (!props) {
    return { notFound: true };
  }

  return {
    props: {
      ...props,
      pagePublishedTime: "2026-08-05T00:00:00+09:00",
      // CDN_URL이 없는 환경에서 "undefined/ogimage.png"가 og:image로 나가지 않게 한다
      ...(process.env.NEXT_PUBLIC_CDN_URL
        ? { pageOgImage: `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png` }
        : {}),
    },
  };
};

const RegionParlorsPage = ({ pageJsonLd, ...data }: RegionPageProps) => {
  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <RegionParlorsContainer {...data} />
    </>
  );
};

export default RegionParlorsPage;
